import { ChangeEvent, MouseEvent, useState } from 'react';

import { FLButton, Title } from '@shared/ui';

import { useAppDispatch, useAppSelector } from '@shared/model';
import { selectGenerationLoading, selectGenerationStateId, useSendEmailsMutation } from '@entities/generation-state';
import classes from './EmailSender.tailwind';
import toast from 'react-hot-toast';
import {
  resetIsSendingEmail,
  resetSendingErrors,
  resetSendingProgress,
  selectIsSendingEmails,
  setIsSendingEmail,
  setSendingState,
} from '@entities/sender-state/model';
import { useSelector } from 'react-redux';
import { setIsSendingReportShown } from '@pages/invoice-generator/shared/model';
import { setSendingStartedAt } from '@pages/invoice-generator/shared/model';
import { DownloadInvoicesButton } from '@widgets/download-invoices-button/DownloadInvoicesButton';

export const EmailSender = () => {
  const dispatch = useAppDispatch();
  const [sendEmailAsync] = useSendEmailsMutation();
  const isSendingEmails = useSelector(selectIsSendingEmails);
  const isGenerationLoading = useSelector(selectGenerationLoading);
  const stateId = useAppSelector(selectGenerationStateId);
  const [subject, setSubject] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const handleSendEmail = async (event: MouseEvent<EventTarget>) => {
    event.preventDefault();

    if (isButtonDisabled) {
      return;
    }

    dispatch(resetSendingErrors());
    dispatch(resetSendingProgress());
    dispatch(setSendingStartedAt(new Date().getTime()));

    if (subject) {
      setIsButtonDisabled(true);
      try {
        const { data } = (await sendEmailAsync({
          id: stateId,
          subject,
        })) as unknown as { data: { emailSendingStateId: string } };

        dispatch(setSendingState(data.emailSendingStateId));
        dispatch(setIsSendingEmail(true));
        dispatch(setIsSendingReportShown(true));

        toast.loading('Sending emails...', {
          duration: 3000,
        });
      } catch {
        dispatch(setIsSendingReportShown(false));

        toast.error('Failed to begin sending invoices. \n Please, try again later.', {
          style: {
            textAlign: 'center',
          },
        });

        dispatch(resetIsSendingEmail());
      } finally {
        setIsButtonDisabled(false);
      }
    } else {
      setIsButtonDisabled(false);

      toast.error('Please, fill in the subject field.', {
        style: {
          textAlign: 'center',
        },
      });
    }
  };

  return (
    <div className={classes.emailSenderContainer} data-id="email-sender">
      <Title text="Send & download invoices" className="text-black text-2xl" />
      <form>
        <label className={classes.labelStyle}>
          <p>Subject:</p>
          <textarea
            value={subject}
            className={classes.textareaStyle}
            rows={4}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setSubject(e.target.value)}
            data-id="textarea-Subject"
          />
        </label>

        <div className={classes.buttonsContainer}>
          <FLButton
            type="primary"
            onClick={handleSendEmail}
            text={isSendingEmails ? 'Sending...' : 'Send'}
            classes={classes.sendEmailButtonClasses}
            data-id="button-Send"
            disabled={isSendingEmails || isGenerationLoading || isButtonDisabled}
          />
          <DownloadInvoicesButton />
        </div>
      </form>
    </div>
  );
};
